import React, { useEffect, useRef, useState, useMemo } from "react";
import { reloadOnceForChunkError } from "@/components/ui/chunkReload";
import { AlertTriangle, Loader2 } from "lucide-react";

export const DEFAULT_VL_SCHEMA = "https://vega.github.io/schema/vega-lite/v5.json";
export const DEFAULT_VEGA_SCHEMA = "https://vega.github.io/schema/vega/v5.json";

export const determineVegaMode = (spec, finalSpec) => {
  const schema = finalSpec?.$schema || "";
  if (schema.includes("/vega/v") && !schema.includes("/vega-lite/")) {
    return "vega";
  }
  if (schema.includes("/vega-lite/")) {
    return "vega-lite";
  }
  const typeStr = (spec?.type || "").toLowerCase().trim();
  if (typeStr === "vega") {
    return "vega";
  }
  // Structural heuristics: Vega uses an array of marks/signals; Vega-Lite uses singular mark & encoding
  if (Array.isArray(finalSpec?.marks) || Array.isArray(finalSpec?.signals)) {
    return "vega";
  }
  return "vega-lite";
};

// Lazy-load vega-embed only when VegaAdapter mounts to avoid bundle bloat
let cachedVegaEmbed = null;
export const getVegaEmbed = async () => {
  if (cachedVegaEmbed) return cachedVegaEmbed;
  const mod = await import("vega-embed");
  cachedVegaEmbed = mod.default || mod;
  return cachedVegaEmbed;
};

const BRAND_CONFIG = {
  background: "transparent",
  font: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  view: { stroke: null },
  range: {
    category: ["#4F46E5", "#F97316", "#10B981", "#EC4899", "#0EA5E9", "#8B5CF6", "#F59E0B"],
  },
  axis: {
    labelColor: "#4B5563",
    titleColor: "#374151",
    gridColor: "#F3F4F6",
    domainColor: "#E5E7EB",
  },
};

const VegaAdapter = ({ spec, containerRef }) => {
  const mountRef = useRef(null);
  const viewRef = useRef(null);
  const [renderError, setRenderError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const prevSpecStringRef = useRef(null);

  const specString = useMemo(() => {
    try {
      return JSON.stringify(spec);
    } catch (e) {
      return "";
    }
  }, [spec]);

  useEffect(() => {
    // If the spec content has not changed and the view is already mounted, do not re-instantiate
    if (prevSpecStringRef.current === specString && viewRef.current) {
      return;
    }
    prevSpecStringRef.current = specString;
    setRenderError(null);
    setIsLoading(true);

    if (!mountRef.current || !spec) {
      setIsLoading(false);
      return;
    }

    // Use nested spec if wrapped inside geo-chart envelope
    const targetSpec = spec.spec || spec;
    const finalSpec = { ...targetSpec };

    // Propagate envelope height (e.g. from fullscreen modal config.height = 450)
    if (spec.config?.height && typeof spec.config.height === "number") {
      finalSpec.height = spec.config.height;
    }

    const mode = determineVegaMode(spec, finalSpec);

    // Default to responsive container width if not explicitly defined (only for Vega-Lite mode)
    if (!finalSpec.width && mode === "vega-lite") {
      finalSpec.width = "container";
    }

    // Inject envelope data only when the nested spec has no data, or populate raw Vega named dataset
    if (Array.isArray(spec.data) && spec.data.length > 0) {
      if (Array.isArray(finalSpec.data)) {
        // Raw Vega spec with named datasets array - clone array and object to prevent mutating props
        finalSpec.data = finalSpec.data.map((dataset, idx) => {
          if (idx === 0 && !dataset.values && !dataset.url) {
            return { ...dataset, values: spec.data };
          }
          return dataset;
        });
      } else if (!finalSpec.data) {
        // Vega-Lite spec with no data property defined
        finalSpec.data = { values: spec.data };
      } else if (typeof finalSpec.data === "object" && !finalSpec.data.values && !finalSpec.data.url) {
        // Vega-Lite spec with empty data object - shallow clone object to prevent mutating props
        finalSpec.data = { ...finalSpec.data, values: spec.data };
      }
    }

    // Deep merge brand theme with custom user configs to preserve axis colors, ranges, and fonts
    finalSpec.config = {
      ...BRAND_CONFIG,
      ...finalSpec.config,
      view: {
        ...BRAND_CONFIG.view,
        ...(finalSpec.config?.view || {}),
      },
      range: {
        ...BRAND_CONFIG.range,
        ...(finalSpec.config?.range || {}),
      },
      axis: {
        ...BRAND_CONFIG.axis,
        ...(finalSpec.config?.axis || {}),
      },
      title: {
        ...(BRAND_CONFIG.title || {}),
        ...(finalSpec.config?.title || {}),
      },
      legend: {
        ...(BRAND_CONFIG.legend || {}),
        ...(finalSpec.config?.legend || {}),
      },
    };

    if (!finalSpec.$schema) {
      finalSpec.$schema = mode === "vega" ? DEFAULT_VEGA_SCHEMA : DEFAULT_VL_SCHEMA;
    }

    let isCancelled = false;

    Promise.all([getVegaEmbed(), import("vega-interpreter")])
      .then(([vegaEmbed, vegaInterpreter]) => {
        if (isCancelled || !mountRef.current) return null;
        return vegaEmbed(mountRef.current, finalSpec, {
          renderer: "canvas", // Uses canvas so AssistantChartCard PNG snapshot export works directly
          actions: false,
          mode,
          ast: true,
          expr: vegaInterpreter.expressionInterpreter,
        });
      })
      .then((result) => {
        if (!result) return;
        if (!isCancelled) {
          viewRef.current = result.view;
          setIsLoading(false);
        } else {
          result.view.finalize();
        }
      })
      .catch((err) => {
        console.error("VegaEmbed failed:", err);
        if (!isCancelled) {
          reloadOnceForChunkError(err);
          setRenderError(err?.message || "Failed to render visual analytics chart.");
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
      if (viewRef.current) {
        viewRef.current.finalize();
        viewRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specString]);

  if (renderError) {
    return (
      <div
        ref={containerRef}
        className="assistant-chart-render-wrapper vega-render-wrapper p-3 text-center"
        role="alert"
        aria-live="polite"
      >
        <div className="d-flex flex-column align-items-center justify-content-center py-4">
          <AlertTriangle size={28} className="text-warning mb-2" />
          <h6 className="text-secondary mb-1">Visualization Rendering Error</h6>
          <p className="text-muted small mb-0" style={{ maxWidth: "420px" }}>
            {renderError}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="assistant-chart-render-wrapper vega-render-wrapper" style={{ width: "100%", overflowX: "auto" }}>
      {isLoading && (
        <div className="d-flex align-items-center justify-content-center py-5 text-muted small" data-testid="vega-chart-loading">
          <Loader2 size={18} className="spin-icon me-2 text-primary" aria-hidden="true" />
          <span>Rendering visualization...</span>
        </div>
      )}
      <div
        ref={mountRef}
        style={{
          width: "100%",
          minHeight: "260px",
          visibility: isLoading ? "hidden" : "visible",
          height: isLoading ? "0px" : "auto",
        }}
      />
    </div>
  );
};

export default VegaAdapter;
